
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BirthdatePicker } from "../BirthdatePicker";
import { Phone, MapPin, ShieldPlus, Heart } from "lucide-react";

interface CompleteProfileFormFieldsProps {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  birthdate: string;
  setBirthdate: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  emergencyPhone: string;
  setEmergencyPhone: (value: string) => void;
  healthInsurance: string;
  setHealthInsurance: (value: string) => void;
  fieldErrors: {
    firstName: boolean;
    lastName: boolean;
    birthdate: boolean;
  };
  validateField: (field: string, value: string) => boolean;
}

export const CompleteProfileFormFields = ({
  firstName, setFirstName,
  lastName, setLastName,
  birthdate, setBirthdate,
  phone, setPhone,
  address, setAddress,
  emergencyPhone, setEmergencyPhone,
  healthInsurance, setHealthInsurance,
  fieldErrors, validateField
}: CompleteProfileFormFieldsProps) => {
  const optionalInputClass = "bg-[#253B3D] text-white border-2 border-[#2196A3] focus:border-[#00C1D6] transition-colors";

  return (
    <>
      <div>
        <Label htmlFor="firstName" className="text-white">Nombre</Label>
        <Input
          id="firstName"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            if (fieldErrors.firstName && e.target.value.trim()) validateField('firstName', e.target.value);
          }}
          onBlur={() => validateField('firstName', firstName)}
          placeholder="Tu nombre"
          required
          className={`bg-[#253B3D] text-white border-2 transition-colors ${
            fieldErrors.firstName ? 'border-red-500 focus:border-red-400' : 'border-[#2196A3] focus:border-[#00C1D6]'
          }`}
        />
        {fieldErrors.firstName && <p className="text-red-400 text-xs mt-1">El nombre es obligatorio</p>}
      </div>
      <div>
        <Label htmlFor="lastName" className="text-white">Apellido</Label>
        <Input
          id="lastName"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            if (fieldErrors.lastName && e.target.value.trim()) validateField('lastName', e.target.value);
          }}
          onBlur={() => validateField('lastName', lastName)}
          placeholder="Tu apellido"
          required
          className={`bg-[#253B3D] text-white border-2 transition-colors ${
            fieldErrors.lastName ? 'border-red-500 focus:border-red-400' : 'border-[#2196A3] focus:border-[#00C1D6]'
          }`}
        />
        {fieldErrors.lastName && <p className="text-red-400 text-xs mt-1">El apellido es obligatorio</p>}
      </div>
      <div>
        <Label htmlFor="birthdate" className="text-white">Fecha de Nacimiento</Label>
        <BirthdatePicker
          value={birthdate}
          onChange={(value) => {
            setBirthdate(value);
            if (fieldErrors.birthdate && value.trim()) validateField('birthdate', value);
          }}
          required
        />
        {fieldErrors.birthdate && <p className="text-red-400 text-xs mt-1">La fecha de nacimiento es obligatoria</p>}
      </div>

      {/* Campos opcionales */}
      <div>
        <Label htmlFor="phone" className="text-white flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5 text-[#00C1D6]" /> Teléfono <span className="text-gray-400 text-xs font-normal">(opcional)</span>
        </Label>
        <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Tu número de teléfono" className={optionalInputClass} />
      </div>
      <div>
        <Label htmlFor="address" className="text-white flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-[#00C1D6]" /> Dirección <span className="text-gray-400 text-xs font-normal">(opcional)</span>
        </Label>
        <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Tu dirección" className={optionalInputClass} />
      </div>
      <div>
        <Label htmlFor="emergencyPhone" className="text-white flex items-center gap-1.5">
          <ShieldPlus className="h-3.5 w-3.5 text-[#00C1D6]" /> Teléfono de Emergencia <span className="text-gray-400 text-xs font-normal">(opcional)</span>
        </Label>
        <Input id="emergencyPhone" value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} placeholder="Número de emergencia" className={optionalInputClass} />
      </div>
      <div>
        <Label htmlFor="healthInsurance" className="text-white flex items-center gap-1.5">
          <Heart className="h-3.5 w-3.5 text-[#00C1D6]" /> Obra Social <span className="text-gray-400 text-xs font-normal">(opcional)</span>
        </Label>
        <Input id="healthInsurance" value={healthInsurance} onChange={(e) => setHealthInsurance(e.target.value)} placeholder="Tu obra social" className={optionalInputClass} />
      </div>
    </>
  );
};
